import { Suspense, lazy, useEffect, useMemo } from 'react';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './AppInsights';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import Footer from './components/Footer/Footer';
import { selectCommon, setIsTablet } from './store/commonSlice';
import { useAppDispatch, useAppSelector } from './store/hooks';
import debounce from 'lodash.debounce';
import { ErrorBoundary } from 'react-error-boundary';
import { getLocationPathArray } from './utils/utilFunctions';
import { selectApplicants } from './store/applicantsSlice';

const Landing = lazy(() => import('./containers/Landing/Landing'));
const AboutYou = lazy(() => import('./containers/AboutYou/AboutYou'));
const VerifyIdentity = lazy(
  () => import('./containers/VerifyIdentity/VerifyIdentity')
);
const TaxDetails = lazy(() => import('./containers/TaxDetails/TaxDetails'));
const AccountDetails = lazy(
  () => import('./containers/AccountDetails/AccountDetails')
);
const FinancialDetails = lazy(
  () => import('./containers/FinanicalDetails/FinanicalDetails')
);
const NextApplicant = lazy(
  () => import('./containers/NextApplicant/NextApplicant')
);
const Review = lazy(() => import('./containers/Review/Review'));
const Confirmation = lazy(
  () => import('./containers/Confirmation/Confirmation')
);
const Retrieval = lazy(() => import('./containers/Retrieval/Retrieval'));
const ErrorPage = lazy(() => import('./containers/ErrorPage/ErrorPage'));
const ScrollToTop = lazy(() => import('./components/ScrollToTop/ScrollToTop'));
const FetchReferenceData = lazy(
  () => import('./components/FetchReferenceData/FetchReferenceData')
);
const ErrorBanner = lazy(() => import('./components/ErrorBanner/ErrorBanner'));
const PageLoading = lazy(() => import('./components/PageLoading/PageLoading'));
const InterestChangeModal = lazy(
  () => import('./components/InterestChangeModal/InterestChangeModal')
);

function App(): JSX.Element {
  const dispatch = useAppDispatch();
  const common = useAppSelector(selectCommon);
  const interestRateChanged = common.interestRateChanged;
  const applicants = useAppSelector(selectApplicants);
  const currentApplicantIndex = applicants.currentApplicantIndex;
  const allowedRoutes = common.referenceData!.accountTypes.reduce(function (
    result,
    item
  ) {
    result += item.route + '|';
    return result;
  },
  '');

  /* Error handler for ErrorBoundary */
  const errorHandler = (
    error: Error,
    info: { componentStack: string }
  ): void => {
    //TODO: can log error to an error logging client
    console.log(error, info.componentStack);
    window.location.pathname = '/loading-error';
  };

  /* Add window resize listener to detect tablet */
  const debouncedFunction = (): void => {
    if (window.innerWidth <= 768) {
      dispatch(setIsTablet(true));
    } else {
      dispatch(setIsTablet(false));
    }
  };

  // useMemo: keep one instance of the debounced function between component re-renderings
  const debouncedHandler = useMemo(
    () => debounce(debouncedFunction, 250),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /* Prevent window to unload (except Landing page, Confirmation page & Error page) */
  const windowCloseHandler = (e: BeforeUnloadEvent): string => {
    const pathArray = getLocationPathArray();
    if (pathArray.length > 1 && !pathArray.includes('confirmation')) {
      e.preventDefault();
      return (e.returnValue =
        'Changes that you made may not be saved. Are you sure you want to reload site? ');
    } else {
      return '';
    }
  };

  /* Prevent window to navigate back (using browser back button)*/
  const windowNavigationHandler = (e: PopStateEvent): void => {
    e.preventDefault();
    window.history.pushState(null, document.title, window.location.href);
  };

  useEffect(() => {
    window.addEventListener('resize', debouncedHandler);
    window.addEventListener('beforeunload', windowCloseHandler);
    window.addEventListener('popstate', windowNavigationHandler, false);

    return (): void => {
      // Stop  the invocation of the debounced function after unmounting
      debouncedHandler.cancel();

      window.removeEventListener('resize', debouncedHandler);
      window.removeEventListener('beforeunload', windowCloseHandler);
      window.removeEventListener('popstate', windowNavigationHandler);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Router>
      <AppInsightsContext.Provider value={reactPlugin}>
        <Suspense fallback={<></>}>
          <ErrorBoundary FallbackComponent={ErrorPage} onError={errorHandler}>
            {common.errorBannerType && (
              <ErrorBanner errorType={common.errorBannerType} />
            )}
            <FetchReferenceData />
            <ScrollToTop />
            <PageLoading />
            {interestRateChanged && (
              <InterestChangeModal status={interestRateChanged} />
            )}

            <Switch>
              <Route exact path='/'>
                <Redirect to='/everyday' />
              </Route>

              <Route
                exact
                path={`/:accountType(${allowedRoutes})`}
                component={Landing}
              />

              <Route
                path={`/:accountType/applicants/:applicantNumber/about-you`}
                render={(): JSX.Element => (
                  <AboutYou key={currentApplicantIndex} /> // Remount component when navigating from progress bar, so aboutYou will be refreshed with current applicant data
                )}
              />
              <Route
                path={`/:accountType/applicants/:applicantNumber/verify-identity`}
                component={VerifyIdentity}
              />
              <Route
                path={`/:accountType/applicants/:applicantNumber/tax-details`}
                component={TaxDetails}
              />
              <Route
                path={`/:accountType/applicants/:applicantNumber/account-details`}
                component={AccountDetails}
              />
              <Route
                path={`/:accountType/applicants/:applicantNumber/financial-details`}
                component={FinancialDetails}
              />
              <Route
                path={`/:accountType/applicants/:applicantNumber/next-applicant`}
                component={NextApplicant}
              />
              <Route
                path={`/:accountType/applicants/:applicantNumber/review`}
                component={Review}
              />
              <Route
                path={`/:accountType/confirmation`}
                component={Confirmation}
              />
              <Route path={`/retrieval`} component={Retrieval} />
              <Route
                path={`/:errorType(404|retrieval-error|submit-error|loading-error)`}
                component={ErrorPage}
              />

              <Route path='*'>
                <Redirect to='/404' />
              </Route>
            </Switch>
          </ErrorBoundary>
        </Suspense>
        <Footer />
      </AppInsightsContext.Provider>
    </Router>
  );
}

export default App;
